import { FC, useState } from 'react';

import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { Stack, Text } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { OxxioSummaryStepRendering } from '@sitecore/types/OxxioSummaryStep';

import {
  SummaryPaymentDetails,
  SummaryPersonalDetailsOverview,
  SummaryProductsOverview,
  SummaryStepError,
  TermsAndConditions,
} from '../../../shared/components';
import { useSummaryStepLogic } from '../../../shared/hooks';
import { PriceDetailsOverviewOxxio } from '../../components';

interface FormFields {
  iban: string;
}

export const SummaryStep: FC = () => {
  const { OxxioSummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    OxxioSummaryStep: OxxioSummaryStepRendering;
  }>();

  const {
    label,
    flowContext,
    sendToFlowMachine,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    selectedCashbackTexts,
    textualDataKey,
    form,
  } = useSummaryStepLogic<FormFields>({
    extensionMode: 'auto',
    paymentDetailsTextualData: textualData?.paymentDetails,
    promotionTexts: textualData?.promotionTexts,
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful },
  } = form;

  const [costPeriod, setCostPeriod] = useState('costsMonthly');
  void costPeriod;
  const { isPersonalDetailsEditProhibited } = flowContext;

  return (
    <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} />
          <Stack.Item>
            <SummaryPersonalDetailsOverview
              textualData={textualData?.personalDetailsOverview}
              flowContext={flowContext}
              onEditPersonalDetails={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_PERSONAL_DETAILS' })}
              onEditAddress={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_ADDRESS' })}
              onEditMoving={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_MOVING' })}
            />
          </Stack.Item>

          <Stack.Item>
            {selectedOffer && (
              <SummaryProductsOverview
                textualData={textualData}
                offer={selectedOffer}
                flowContext={flowContext}
                textualDataKey={textualDataKey}
                setCostPeriod={setCostPeriod}
                selectedCashbackTexts={selectedCashbackTexts}
                cashbackValue={Number(selectedOffer?.costDiscounts?.cashback?.value ?? 0)}
                cashbackType={selectedOffer?.costDiscounts?.cashback?.type}
                ribbonTheme="oxxio"
                wrapPriceDetails
                priceDetails={
                  <PriceDetailsOverviewOxxio
                    flowContext={flowContext}
                    textualData={textualData}
                    offer={selectedOffer}
                  />
                }
              />
            )}
          </Stack.Item>

          {!isPersonalDetailsEditProhibited && (
            <SummaryPaymentDetails
              textualData={textualData?.paymentDetails}
              register={register}
              ibanError={errors.iban}
            />
          )}

          <TermsAndConditions
            label={label}
            flowContext={flowContext}
            textualData={textualData}
            offer={selectedOffer}
            offerTermsAndConditions={selectedOfferTermsAndConditions}
          />

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>

          {textualData?.content?.energyDisputeCommitteeText?.value && (
            <Text size="BodyXS" color="textLowEmphasis">
              {textualData?.content?.energyDisputeCommitteeText?.value}
            </Text>
          )}
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SummaryStepError
          isNotAuthenticated={isNotAuthenticated}
          isNotAvailable={isNotAvailable}
          isNotFound={isNotFound}
          isOrderFailed={isOrderFailed}
          textualData={textualData?.genericError}
        />
      )}
    </Layout.Content>
  );
};
